<template>
  <div class="attachment-selector">
    <!-- 编辑模式 -->
    <template v-if="!readonly">
      <span class="box">
        <el-input :value="valueDisplayText" :placeholder="placeholder" suffix-icon="el-icon-zoom-in"
          style="width: 240px" size="small" clearable @clear="clear" @click.native="onInputClick" readonly></el-input>
      </span>

      <el-dialog title="选择附件" :visible.sync="visible" width="1000px" append-to-body :close-on-click-modal="false">
        <!-- 筛选区域 -->
        <el-form ref="form" :model="form" size="small" inline>
          <el-form-item prop="attachmentType" label="附件类型：">
            <el-select v-model="form.attachmentType" clearable placeholder="全部类型" style="width: 140px" @change="query">
              <el-option v-for="item in attachmentTypeOpts" :key="item.value" :label="item.label"
                :value="item.value"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item prop="keyword" label="关键词：">
            <el-input v-model="form.keyword" placeholder="标签/标题" clearable style="width: 200px"
              @keyup.enter.native="query"></el-input>
          </el-form-item>

          <el-form-item prop="uploaderId" label="上传者：">
            <el-select v-model="form.uploaderId" clearable filterable placeholder="全部" style="width: 160px"
              @change="query">
              <el-option v-for="u in adminOptions" :key="u.id" :label="formatAdminLabel(u)" :value="u.id"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" style="width: 80px; margin-left: 10px" @click="query" size="small">查询</el-button>
            <el-button style="width: 80px" @click="reset" size="small">重置</el-button>
          </el-form-item>
        </el-form>

        <div style="margin: 15px 0">
          <el-table :data="list" border size="small" max-height="350" ref="table" :highlight-current-row="!multiple"
            @current-change="handleCurrentChange" @selection-change="handleSelectionChange" v-loading="loading">
            <el-table-column v-if="multiple" type="selection" width="50" align="center"></el-table-column>

            <el-table-column v-else width="50" align="center">
              <template slot-scope="scope">
                <el-radio v-model="selectedId" :label="scope.row.id" @click.native.stop
                  @change="() => handleRadioChange(scope.row)"><span></span></el-radio>
              </template>
            </el-table-column>

            <el-table-column prop="attachmentType" label="类型" width="100">
              <template slot-scope="scope">
                <span>{{ scope.row.attachmentType | enumTransfer(attachmentTypeOpts, 'value', 'label') }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="title" label="标题" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column prop="tag" label="标签" width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="description" label="描述" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column label="详情" width="88" align="center">
              <template slot-scope="scope">
                <el-popover placement="left-start" trigger="click" append-to-body
                  popper-class="attachment-preview-popover" :value="activePreviewRowId === scope.row.id"
                  @input="onPreviewPopoverInput(scope.row.id, $event)">
                  <div class="popover-preview-inner">
                    <AttachmentDetail :attachmentType="scope.row.attachmentType" :url="scope.row.url"
                      :bizId="scope.row.bizId" @close="closePreviewPopover">
                    </AttachmentDetail>
                  </div>
                  <el-button slot="reference" type="text" size="small" class="preview-btn" @click.stop>
                    预览
                  </el-button>
                </el-popover>
              </template>
            </el-table-column>
            <el-table-column prop="uploaderName" label="上传者" width="100"></el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="150"></el-table-column>
          </el-table>

          <el-pagination style="margin-top: 15px; text-align: right" small background layout="total, prev, pager, next"
            :total="total" :page-size="pageSize" :current-page.sync="currentPage"
            @current-change="handlePageChange"></el-pagination>
        </div>

        <div class="footer">
          <el-button type="primary" size="small" @click="handleOk">
            确定{{ multiple && selectedRows.length > 0 ? `(${selectedRows.length})` : '' }}
          </el-button>
          <el-button size="small" @click="visible = false">取消</el-button>
        </div>
      </el-dialog>
    </template>

    <!-- 只读模式 -->
    <template v-else>
      <span v-if="valueDisplayText" class="readonly-text">{{ valueDisplayText }}</span>
      <span v-else class="readonly-empty">-</span>
    </template>
  </div>
</template>

<script>
import { getWorkflowAttachmentList } from "@/api/workflow";
import { getAdminList } from "@/api/admin";
import { Toolkit } from "@/util";
import AttachmentDetail from "./AttachmentDetail.vue";
export default {
  name: "AttachmentSelector",
  components: { AttachmentDetail },
  props: {
    // 支持 v-model
    value: {
      type: [Number, String, Object, Array],
      default: null
    },
    // 是否只读
    readonly: {
      type: Boolean,
      default: false
    },
    // 占位文本
    placeholder: {
      type: String,
      default: "请选择附件"
    },
    // 附件类型选项（从父组件传入或从store获取）
    attachmentTypeOpts: {
      type: Array,
      default: () => []
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      adminOptions: [{ id: -1, name: "系统" }],
      adminOptionsLoaded: false,
      form: {
        attachmentType: null,
        keyword: "",
        uploaderId: null
      },
      selectedId: null,
      selectedRow: null,
      selectedRows: [], // 多选模式使用
      /** 当前展开「预览」popover 的行 id，同一时间只展开一个 */
      activePreviewRowId: null
    };
  },
  computed: {
    /** 编辑区输入框与只读展示共用：仅由 value + multiple 决定 */
    valueDisplayText() {
      if (this.multiple) {
        if (!Array.isArray(this.value) || this.value.length === 0) {
          return "";
        }
        return `已选择 ${this.value.length} 个附件`;
      }
      if (typeof this.value === "object") {
        return this.formatAttachment(this.value);
      }
      return "";
    }
  },
  watch: {
    visible(val) {
      if (!val) {
        this.activePreviewRowId = null;
      } else {
        this.ensureAdminOptions();
      }
    },
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        if (this.multiple) {
          if (Array.isArray(val)) {
            this.selectedRows = [...val];
          } else {
            this.selectedRows = [];
          }
          return;
        }
        // 单选：null / 附件对象 / 外层偶发传入仅含一项的数组
        if (val == null || val === "") {
          this.selectedId = null;
          this.selectedRow = null;
          return;
        }
        if (Array.isArray(val)) {
          if (val.length === 1 && val[0] && typeof val[0] === "object") {
            this.selectedId = val[0].id;
            this.selectedRow = val[0];
          } else {
            this.selectedId = null;
            this.selectedRow = null;
          }
          return;
        }
        if (typeof val === "object") {
          this.selectedId = val.id;
          this.selectedRow = val;
          return;
        }
        this.selectedId = null;
        this.selectedRow = null;
      }
    }
  },
  methods: {
    formatAdminLabel(u) {
      return `${(u && u.name) || ""}`;
    },
    ensureAdminOptions() {
      if (this.adminOptionsLoaded) return;
      this.loadAdminList();
    },
    loadAdminList() {
      const param = {
        page: { size: 500 },
        param: { normalUser: true }
      };
      getAdminList(param).then(res => {
        const list = res.data.records || [];
        list.unshift({ id: -1, name: "系统" });
        this.adminOptions = list;
        this.adminOptionsLoaded = true;
      });
    },
    formatAttachment(row) {
      if (!row) return "";
      const typeText = this.getAttachmentTypeText(row.attachmentType);
      const title = row.title || "";
      if (!typeText && !title) return "请选择附件";
      if (!typeText) return title;
      if (!title) return typeText;
      return `${typeText} - ${title}`;
    },
    getAttachmentTypeText(type) {
      if (!type) return "";
      const found = this.attachmentTypeOpts.find(
        item => item.value === type || item.value === String(type)
      );
      return found ? found.label : type;
    },
    query() {
      this.currentPage = 1;
      this.loadList();
    },
    reset() {
      this.form = {
        attachmentType: null,
        keyword: "",
        uploaderId: null
      };
      this.query();
    },
    loadList() {
      this.loading = true;
      const params = {
        current: this.currentPage,
        size: this.pageSize,
        attachmentType: this.form.attachmentType,
        keyword: this.form.keyword,
        uploaderId: this.form.uploaderId
      };
      getWorkflowAttachmentList(Toolkit.filterEmpty(params))
        .then(res => {
          this.list = res.data.records || [];
          this.total = res.data.total || 0;

          // 多选模式：设置已选项
          if (this.multiple && this.selectedRows.length > 0) {
            this.$nextTick(() => {
              this.list.forEach(row => {
                if (this.selectedRows.some(s => s.id === row.id)) {
                  this.$refs.table.toggleRowSelection(row, true);
                }
              });
            });
          }

          // 单选模式：高亮当前选中项
          if (!this.multiple && this.selectedId) {
            const found = this.list.find(item => item.id === this.selectedId);
            if (found) {
              this.selectedRow = found;
            }
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadList();
    },
    handleCurrentChange(row) {
      if (!this.multiple && row) {
        this.selectedId = row.id;
        this.selectedRow = row;
      }
    },
    handleSelectionChange(selection) {
      if (this.multiple) {
        this.selectedRows = selection;
      }
    },
    handleRadioChange(row) {
      this.selectedId = row.id;
      this.selectedRow = row;
    },
    handleOk() {
      if (this.multiple) {
        // 多选模式
        if (this.selectedRows.length > 0) {
          this.$emit("input", [...this.selectedRows]);
          this.$emit("change", [...this.selectedRows]);
          this.visible = false;
        } else {
          this.$message.warning("请至少选择一个附件");
        }
      } else {
        // 单选模式
        if (this.selectedRow) {
          this.$emit("input", this.selectedRow);
          this.$emit("change", this.selectedRow);
          this.visible = false;
        } else {
          this.$message.warning("请选择一个附件");
        }
      }
    },
    clear() {
      this.selectedId = null;
      this.selectedRow = null;
      this.selectedRows = [];
      this.$emit("input", this.multiple ? [] : null);
      this.$emit("change", this.multiple ? [] : null);
    },
    onInputClick(e) {
      // 点击清空按钮时不打开弹窗（阻止冒泡导致的误触发）
      if (e.target.closest(".el-input__clear")) {
        return;
      }
      this.visible = true;
      this.currentPage = 1;
      this.loadList();
    },
    onPreviewPopoverInput(rowId, visible) {
      if (visible) {
        this.activePreviewRowId = rowId;
      } else if (this.activePreviewRowId === rowId) {
        this.activePreviewRowId = null;
      }
    },
    closePreviewPopover() {
      this.activePreviewRowId = null;
    }
  }
};
</script>

<style lang="less" scoped>
.attachment-selector {
  display: inline-block;
}

.box {
  display: inline-block;
}

.footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 15px;
  border-top: 1px solid #ebeef5;
}

.readonly-text {
  color: #606266;
}

.readonly-empty {
  color: #c0c4cc;
}

// 单选框样式调整
/deep/ .el-radio__label {
  display: none;
}

.preview-btn {
  padding: 0;
}
</style>

<style lang="less">
/* append-to-body 的浮层不在 scoped 内，单独写 */
.attachment-preview-popover {
  max-height: 72vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 12px 14px;
  box-sizing: border-box;
}

.attachment-preview-popover .popover-preview-inner {
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}
</style>
